import { useMemo, useCallback, useReducer } from 'react';
import { ModalSheet } from '@packages/ui/composites';
import { invalidateAsyncSelectOptions } from '@packages/cache-manager';
import { ActionRegistry } from '@packages/table-builder';
import type { ActionExecutionContext, ComposeModalConfig, ComposeModalMode } from '@packages/table-builder';
import { ControlRenderer, useControlRendererRef } from '@packages/compose/core';
import {
  buildComposeEffectiveMeta,
  resolveComposeModalMode,
  resolveComposeModalTitle,
} from '@packages/compose/constants/composeModalMode';
import { queryClient } from '@/queryClient';
import {
  deriveFormSessionKey,
  dismissComposeFormSideStores,
} from '@packages/form-engine/session/composeFormSession';
import { buildComposeControlRendererKey } from './composeControlRendererKey';
import { shouldShowComposeViewEditAction } from './composeViewModeFooter';

// --- In-place mode switch reducer (Strategy B) ---
type ModalModeState = { mode: ComposeModalMode };
type ModalModeAction =
  | { type: 'SWITCH_TO_EDIT' }
  | { type: 'RESET'; mode: ComposeModalMode };

function modalModeReducer(state: ModalModeState, action: ModalModeAction): ModalModeState {
  switch (action.type) {
    case 'SWITCH_TO_EDIT':
      return { mode: 'edit' };
    case 'RESET':
      return { mode: action.mode };
  }
}

export interface ComposeModalProps {
  actionId: string;
  context: ActionExecutionContext;
  isOpen: boolean;
  onClose: () => void;
  onExitComplete?: () => void;
  /** Unique stack entry key — forces ControlRenderer remount per modal open. */
  composeInstanceKey: string;
}

export function ComposeModal({
  actionId,
  context,
  isOpen,
  onClose,
  onExitComplete,
  composeInstanceKey,
}: ComposeModalProps) {
  const meta = context.meta as unknown as ComposeModalConfig | undefined;
  const controlRef = useControlRendererRef();

  const actions = meta?.actions;
  const primaryAction = actions?.[0];
  const secondaryAction = actions?.[1];

  const initialMode = resolveComposeModalMode(meta, primaryAction?.intent);
  const [modeState, dispatchMode] = useReducer(modalModeReducer, { mode: initialMode });
  const currentMode = modeState.mode;

  const params = useMemo(() => ({ id: context.pk, ...(context.params ?? {}) }), [context.pk, context.params]);

  const formSessionKey = useMemo(
    () => deriveFormSessionKey(currentMode, params as Record<string, string>),
    [currentMode, params],
  );

  const formSchemaId =
    typeof meta?.formSchemaId === 'string' && meta.formSchemaId.length > 0
      ? meta.formSchemaId
      : undefined;

  // Always emit canonical meta.mode (create/edit/view) — legacy intents may still pass `add`.
  const effectiveMeta = useMemo(
    () =>
      buildComposeEffectiveMeta(meta as unknown as Record<string, unknown>, currentMode, {
        formSessionKey,
        composeInstanceKey,
        ...(formSchemaId ? { formSchemaId } : {}),
      }),
    [meta, currentMode, formSessionKey, composeInstanceKey, formSchemaId],
  );

  const controlRendererKey = buildComposeControlRendererKey(
    actionId,
    formSessionKey,
    composeInstanceKey,
  );

  const isFormModal = controlRef.formId != null;

  const handleSubmitSuccess = useCallback(() => {
    const asyncSelectActionIds = (
      meta as (ComposeModalConfig & { asyncSelectActionIds?: string[] }) | undefined
    )?.asyncSelectActionIds;
    if (asyncSelectActionIds?.length) {
      invalidateAsyncSelectOptions(queryClient, asyncSelectActionIds);
    }
    onClose();
  }, [meta, onClose]);

  const emitAction = useCallback((modalActionId: string) => {
    onClose();
    void ActionRegistry.executeAction(modalActionId, context);
  }, [onClose, context]);

  // In-place mode switch: view → edit (no close/reopen)
  const handleViewToEdit = useCallback(() => {
    dispatchMode({ type: 'SWITCH_TO_EDIT' });
  }, []);

  const handleAction = useCallback(() => {
    if (!primaryAction) return;

    if (currentMode === 'view' && primaryAction.intent === 'edit') {
      if (isFormModal) {
        handleViewToEdit();
        return;
      }
      emitAction(primaryAction.id);
      return;
    }

    emitAction(primaryAction.id);
  }, [primaryAction, currentMode, isFormModal, handleViewToEdit, emitAction]);

  const handleSecondaryAction = useCallback(() => {
    if (secondaryAction) emitAction(secondaryAction.id);
  }, [secondaryAction, emitAction]);

  const showViewModeEdit = shouldShowComposeViewEditAction(currentMode, primaryAction);

  const handleExitComplete = useCallback(() => {
    dismissComposeFormSideStores({
      formSessionKey,
      mode: currentMode,
      formSchemaId,
    });
    const exitActionId = meta?.onExitCompleteActionId;
    if (exitActionId) {
      void ActionRegistry.executeAction(exitActionId, { ...context, params });
    }
    onExitComplete?.();
  }, [formSessionKey, currentMode, formSchemaId, onExitComplete, meta?.onExitCompleteActionId, context, params]);

  return (
    <ModalSheet
      open={isOpen}
      onOpenChange={(o) => !o && onClose()}
      onExitComplete={handleExitComplete}
      title={resolveComposeModalTitle(meta, currentMode)}
      subtitle={meta?.subtitle}
      size={(meta?.size ?? 'xl') as any}
      semanticId={meta?.semanticId ?? `${actionId}Modal`}
      loading={controlRef.isLoading}
      mode={currentMode}
      // Form footer wiring — only active when in edit/add mode AND the block is a form
      actionForm={isFormModal && currentMode !== 'view' ? controlRef.formId : undefined}
      dirty={isFormModal && currentMode !== 'view' ? controlRef.isDirty : undefined}
      actionLoading={isFormModal && currentMode !== 'view' ? controlRef.isSubmitting : undefined}
      actionDisabled={isFormModal && currentMode === 'edit' ? !controlRef.isDirty : undefined}
      actionLabel={
        showViewModeEdit
          ? (primaryAction?.label ?? 'Edit')
          : currentMode !== 'view'
            ? (meta?.actionLabel ?? (!isFormModal ? primaryAction?.label : undefined))
            : undefined
      }
      showAction={currentMode === 'view' ? showViewModeEdit : true}
      cancelLabel={secondaryAction?.label}
      onAction={
        showViewModeEdit
          ? handleAction
          : currentMode !== 'view' && !isFormModal
            ? handleAction
            : undefined
      }
      onSecondaryAction={secondaryAction ? handleSecondaryAction : undefined}>
      {isOpen && (
        <ControlRenderer
          key={controlRendererKey}
          actionId={actionId}
          params={params}
          meta={effectiveMeta}
          handle={controlRef}
          onSuccess={handleSubmitSuccess}
        />
      )}
    </ModalSheet>
  );
}
